import * as THREE from 'three';

/** Material `#define` key — positive bias samples blurrier mips earlier (kills brushed-metal moiré). */
export const C3D_TEX_LOD_BIAS_DEFINE = 'C3D_TEX_LOD_BIAS';

const C3D_TEX_LOD_BIAS_FN = 'c3dTexLodBias';

/**
 * Screen-space adaptive mip bias (fragment only — uses dFdx/dFdy).
 * Close-up: a little bias so grain stays sharp. Mid: more, to kill weave moiré.
 * Far: drop extra bias — the GPU already picked a high mip; multiplying 1.35×
 * here is what made Metal Mesh look washed-out on phones when zoomed out.
 */
const C3D_TEX_LOD_BIAS_GLSL = /* glsl */ `
#ifndef C3D_TEX_LOD_BIAS_FN
#define C3D_TEX_LOD_BIAS_FN
float c3dTexLodBias( const in vec2 uv ) { // C3D_LOD_ADAPT_V2
  float b = ${C3D_TEX_LOD_BIAS_DEFINE};
  vec2 dx = dFdx( uv );
  vec2 dy = dFdy( uv );
  float rho2 = max( dot( dx, dx ), dot( dy, dy ) );
  float tMid = smoothstep( 8.0e-8, 4.0e-5, rho2 );
  float tFar = smoothstep( 1.5e-4, 8.0e-4, rho2 );
  float scale = mix( 0.18, 0.72, tMid );
  scale = mix( scale, 0.28, tFar );
  return b * scale;
}
#endif
`;

function ensureLodBiasHelper(src: string): string {
  if (src.includes(`float ${C3D_TEX_LOD_BIAS_FN}`)) return src;
  return C3D_TEX_LOD_BIAS_GLSL + src;
}

function withAdaptiveLodSample(src: string, sampler: string, uv: string): string {
  const plain = `texture2D( ${sampler}, ${uv} )`;
  const constBias = `texture2D( ${sampler}, ${uv}, ${C3D_TEX_LOD_BIAS_DEFINE} )`;
  const adapted = `texture2D( ${sampler}, ${uv}, ${C3D_TEX_LOD_BIAS_FN}( ${uv} ) )`;
  return src.split(constBias).join(adapted).split(plain).join(adapted);
}

/**
 * Patch Three ShaderChunks so material maps honor `C3D_TEX_LOD_BIAS`.
 * Bias is scaled by UV screen derivatives so close-ups stay sharp.
 */
export function ensureTextureLodBiasShaderChunks(): void {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const mapFrag = chunk.map_fragment;
  if (mapFrag?.includes('C3D_LOD_ADAPT_V2')) return;

  // Hot reload: rewrite the v1 formula if chunks were already patched this session.
  if (mapFrag?.includes(`float ${C3D_TEX_LOD_BIAS_FN}`)) {
    for (const key of Object.keys(chunk)) {
      const src = chunk[key];
      if (!src || !src.includes(`float ${C3D_TEX_LOD_BIAS_FN}`)) continue;
      chunk[key] = src.replace(
        /float c3dTexLodBias\( const in vec2 uv \) \{[\s\S]*?return b \* mix\([^;]+;\s*\}/,
        `float c3dTexLodBias( const in vec2 uv ) { // C3D_LOD_ADAPT_V2
  float b = ${C3D_TEX_LOD_BIAS_DEFINE};
  vec2 dx = dFdx( uv );
  vec2 dy = dFdy( uv );
  float rho2 = max( dot( dx, dx ), dot( dy, dy ) );
  float tMid = smoothstep( 8.0e-8, 4.0e-5, rho2 );
  float tFar = smoothstep( 1.5e-4, 8.0e-4, rho2 );
  float scale = mix( 0.18, 0.72, tMid );
  scale = mix( scale, 0.28, tFar );
  return b * scale;
}`,
      );
    }
    if (chunk.map_fragment?.includes('C3D_LOD_ADAPT_V2')) return;
  }

  const common = chunk.common;
  if (common && !common.includes(C3D_TEX_LOD_BIAS_DEFINE)) {
    chunk.common =
      `#ifndef ${C3D_TEX_LOD_BIAS_DEFINE}\n#define ${C3D_TEX_LOD_BIAS_DEFINE} 0.0\n#endif\n` + common;
  }

  const samples: Array<[string, string, string]> = [
    ['map_fragment', 'map', 'vMapUv'],
    ['roughnessmap_fragment', 'roughnessMap', 'vRoughnessMapUv'],
    ['metalnessmap_fragment', 'metalnessMap', 'vMetalnessMapUv'],
    ['aomap_fragment', 'aoMap', 'vAoMapUv'],
    ['emissivemap_fragment', 'emissiveMap', 'vEmissiveMapUv'],
    ['specularmap_fragment', 'specularMap', 'vSpecularMapUv'],
    ['alphamap_fragment', 'alphaMap', 'vAlphaMapUv'],
    ['clearcoat_normal_fragment_maps', 'clearcoatNormalMap', 'vClearcoatNormalMapUv'],
    ['normal_fragment_maps', 'normalMap', 'vNormalMapUv'],
    ['lights_physical_fragment', 'anisotropyMap', 'vAnisotropyMapUv'],
  ];

  for (const [key, sampler, uv] of samples) {
    const src = chunk[key];
    if (!src) continue;
    chunk[key] = ensureLodBiasHelper(withAdaptiveLodSample(src, sampler, uv));
  }

  const bump = chunk.bumpmap_pars_fragment;
  if (bump && bump.includes('texture2D( bumpMap, vBumpMapUv )')) {
    let next = ensureLodBiasHelper(bump);
    next = next.replace(
      `texture2D( bumpMap, vBumpMapUv, ${C3D_TEX_LOD_BIAS_DEFINE} ).x`,
      `texture2D( bumpMap, vBumpMapUv, ${C3D_TEX_LOD_BIAS_FN}( vBumpMapUv ) ).x`,
    );
    next = next.replace(
      'texture2D( bumpMap, vBumpMapUv ).x',
      `texture2D( bumpMap, vBumpMapUv, ${C3D_TEX_LOD_BIAS_FN}( vBumpMapUv ) ).x`,
    );
    next = next.replace(
      `texture2D( bumpMap, vBumpMapUv + dSTdx, ${C3D_TEX_LOD_BIAS_DEFINE} ).x`,
      `texture2D( bumpMap, vBumpMapUv + dSTdx, ${C3D_TEX_LOD_BIAS_FN}( vBumpMapUv ) ).x`,
    );
    next = next.replace(
      'texture2D( bumpMap, vBumpMapUv + dSTdx ).x',
      `texture2D( bumpMap, vBumpMapUv + dSTdx, ${C3D_TEX_LOD_BIAS_FN}( vBumpMapUv ) ).x`,
    );
    next = next.replace(
      `texture2D( bumpMap, vBumpMapUv + dSTdy, ${C3D_TEX_LOD_BIAS_DEFINE} ).x`,
      `texture2D( bumpMap, vBumpMapUv + dSTdy, ${C3D_TEX_LOD_BIAS_FN}( vBumpMapUv ) ).x`,
    );
    next = next.replace(
      'texture2D( bumpMap, vBumpMapUv + dSTdy ).x',
      `texture2D( bumpMap, vBumpMapUv + dSTdy, ${C3D_TEX_LOD_BIAS_FN}( vBumpMapUv ) ).x`,
    );
    chunk.bumpmap_pars_fragment = next;
  }
}

/** Format a float for Three material `defines` (must be a valid GLSL token). */
export function formatTexLodBiasDefine(bias: number): string {
  const v = Math.max(0, Math.min(2.5, bias));
  return Number.isFinite(v) ? v.toFixed(3) : '0.000';
}

/**
 * Apply / update `C3D_TEX_LOD_BIAS` on a material. Triggers one recompile when the
 * value changes. Pass 0 to clear the define (stock sampling).
 */
export function applyMaterialTextureLodBias(mat: THREE.Material, bias: number): void {
  ensureTextureLodBiasShaderChunks();
  const defines = (mat.defines ?? {}) as Record<string, string | number | undefined>;
  const next = bias > 0.001 ? formatTexLodBiasDefine(bias) : undefined;
  const prev = defines[C3D_TEX_LOD_BIAS_DEFINE];
  if (next == null) {
    if (prev == null) return;
    delete defines[C3D_TEX_LOD_BIAS_DEFINE];
    mat.defines = defines;
    mat.needsUpdate = true;
    return;
  }
  if (String(prev) === next) return;
  defines[C3D_TEX_LOD_BIAS_DEFINE] = next;
  mat.defines = defines;
  mat.needsUpdate = true;
}

function readImageSize(image: unknown): { w: number; h: number } | null {
  if (!image) return null;
  if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement) {
    const w = image.naturalWidth || image.width;
    const h = image.naturalHeight || image.height;
    return w > 0 && h > 0 ? { w, h } : null;
  }
  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
    return image.width > 0 && image.height > 0 ? { w: image.width, h: image.height } : null;
  }
  if (
    image instanceof HTMLCanvasElement ||
    (typeof OffscreenCanvas !== 'undefined' && image instanceof OffscreenCanvas)
  ) {
    const el = image as { width: number; height: number };
    return el.width > 0 && el.height > 0 ? { w: el.width, h: el.height } : null;
  }
  const el = image as { width?: number; height?: number; videoWidth?: number; videoHeight?: number };
  const w = el.width ?? el.videoWidth;
  const h = el.height ?? el.videoHeight;
  return typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0 ? { w, h } : null;
}

/**
 * Configure filtering so minified maps (distance, grazing angles) stay smooth.
 *
 * Moiré / crawl happens when the GPU samples a dense pattern without a mip pyramid:
 * each pixel would average only a tiny patch of mip0. Fix: `generateMipmaps` +
 * `LinearMipmapLinearFilter` (trilinear), plus anisotropic filtering on oblique surfaces.
 *
 * Anisotropy only applies when minFilter is a *mipmap* filter — set mips before raising
 * `anisotropy`, and keep Max anisotropy at 8–16 in PostFX for fine woven/metal maps.
 *
 * WebGL2 supports NPOT + repeat + mipmaps natively. On WebGL1, Three.js pot-resizes
 * on upload when needed — so we no longer refuse mips for non-power-of-two images
 * (that path was the main cause of sanded-normal sparkle when zooming out).
 */
export function configureStableTextureSampling(tex: THREE.Texture): void {
  // Compressed GPU textures / cube maps manage their own mip chains.
  if ((tex as THREE.CompressedTexture).isCompressedTexture) return;
  if ((tex as THREE.CubeTexture).isCubeTexture) return;

  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  // Cap is raised further per-frame to the renderer max in editor/viewer canvases.
  if (!tex.anisotropy || tex.anisotropy < 16) tex.anisotropy = 16;

  // Only mark dirty when we have pixels; avoids "Texture marked for update but no image data".
  if (readImageSize(tex.image)) {
    tex.needsUpdate = true;
  }
}

/**
 * Normal / bump maps: same mip stack as albedo, but force a GPU re-upload when
 * pixels already exist so clones that inherited a non-mip snapshot get a fresh pyramid.
 */
export function configureNormalMapSampling(tex: THREE.Texture): void {
  configureStableTextureSampling(tex);
  if (readImageSize(tex.image)) {
    tex.needsUpdate = true;
  }
}

/** True when mip/min filter look set for stable minification. */
export function textureHasStableMinFilter(tex: THREE.Texture): boolean {
  if ((tex as THREE.CompressedTexture).isCompressedTexture) return true;
  if ((tex as THREE.CubeTexture).isCubeTexture) return true;
  return (
    tex.generateMipmaps === true &&
    (tex.minFilter === THREE.LinearMipmapLinearFilter ||
      tex.minFilter === THREE.LinearMipmapNearestFilter ||
      tex.minFilter === THREE.NearestMipmapLinearFilter ||
      tex.minFilter === THREE.NearestMipmapNearestFilter)
  );
}

/** UV / channel clones of a CanvasTexture — must re-upload when the source canvas repaints. */
const canvasTextureClones = new WeakMap<THREE.Texture, Set<THREE.Texture>>();

/**
 * After a Text Canvas (or any CanvasTexture) redraws, push the new pixels to UV clones
 * and clear stale `__c3dBakePending` copied at clone time. Engraving opacity/bump go
 * through UV Transform; without this the alphaMap stays on the blank pre-font canvas.
 */
export function syncCanvasTextureClones(source: THREE.Texture): void {
  const clones = canvasTextureClones.get(source);
  if (!clones || clones.size === 0) return;
  const srcPending = !!(source.userData as Record<string, unknown> | undefined)?.__c3dBakePending;
  const srcEpoch =
    ((source.userData as Record<string, unknown> | undefined)?.__c3dContentEpoch as number) || 0;
  for (const clone of clones) {
    if (source.image && clone.image !== source.image) {
      clone.image = source.image;
    }
    const cud = clone.userData as Record<string, unknown>;
    if (!srcPending) {
      delete cud.__c3dBakePending;
      delete cud.__c3dBakeSource;
      delete cud.__c3dBakeStandIn;
    }
    if (srcEpoch > 0) cud.__c3dContentEpoch = srcEpoch;
    clone.needsUpdate = true;
  }
}

/**
 * Copy a texture for UV / channel variants without `Texture.clone()`.
 * Three's clone→copy→toJSON serializes HTMLImageElement via getDataURL (jpg convert)
 * on every material eval — that spammed the console and stalled Wood/Gradient swaps.
 */
export function cloneTextureLight(source: THREE.Texture): THREE.Texture {
  const clone =
    (source as THREE.CanvasTexture).isCanvasTexture === true
      ? new THREE.CanvasTexture(source.image as HTMLCanvasElement)
      : new THREE.Texture(source.image as any);

  clone.mapping = source.mapping;
  clone.wrapS = source.wrapS;
  clone.wrapT = source.wrapT;
  clone.magFilter = source.magFilter;
  clone.minFilter = source.minFilter;
  clone.format = source.format;
  clone.internalFormat = source.internalFormat;
  clone.type = source.type;
  clone.anisotropy = source.anisotropy;
  // CanvasTexture / Texture default to NoColorSpace. Leaving that on an albedo clone
  // uploads RGBA8 and skips sRGB decode → Wood looks blown-out white.
  clone.colorSpace = source.colorSpace;
  clone.flipY = source.flipY;
  clone.generateMipmaps = source.generateMipmaps;
  clone.premultiplyAlpha = source.premultiplyAlpha;
  clone.unpackAlignment = source.unpackAlignment;
  clone.repeat.copy(source.repeat);
  clone.offset.copy(source.offset);
  clone.center.copy(source.center);
  clone.rotation = source.rotation;
  // Always auto-update — copying a stale matrix from the source showed Metal Mesh at 1×
  // scale for a frame before UV Transform "kicked in".
  clone.matrixAutoUpdate = true;
  clone.updateMatrix();
  (clone as THREE.Texture & { channel?: number }).channel =
    (source as THREE.Texture & { channel?: number }).channel ?? 0;
  clone.userData = { ...(source.userData ?? {}) };
  // Always remember the source — CanvasTexture clones share an image (no empty `image`
  // to trigger this) and CPU-bake cache keys used to fall back to the clone UUID,
  // so Wood HSL/roughness re-baked (gloss flash) on every select.
  (clone.userData as Record<string, unknown>).__c3dCloneSource = source;
  if ((source as THREE.CanvasTexture).isCanvasTexture === true) {
    let set = canvasTextureClones.get(source);
    if (!set) {
      set = new Set();
      canvasTextureClones.set(source, set);
    }
    set.add(clone);
  }
  clone.needsUpdate = true;
  return clone;
}

/** Apply UV transform fields and refresh the sampling matrix immediately. */
export function commitTextureUV(tex: THREE.Texture): void {
  tex.matrixAutoUpdate = true;
  tex.updateMatrix();
  tex.needsUpdate = true;
}
